import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, addDoc, updateDoc,
  collection, serverTimestamp, Timestamp
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@services/firebase';
import { ArrowLeft, Save, Loader2, Plus, X, Upload, Image, Wand2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useRef } from 'react';
import api from '@services/api';

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'twitter',   label: 'Twitter / X' },
  { value: 'linkedin',  label: 'LinkedIn' },
  { value: 'facebook',  label: 'Facebook' },
  { value: 'tiktok',    label: 'TikTok' },
  { value: 'mongol',    label: 'Mongol (MN)' },
  { value: 'coderjs',   label: 'CoderJS (X)' },
];

const STATUSES = [
  { value: 'draft',     label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'published', label: 'Published' },
];

export default function AdminSocialEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const fileRef = useRef();

  const [loading, setLoading]         = useState(isEdit);
  const [saving, setSaving]           = useState(false);
  const [uploadPct, setUploadPct]     = useState(null);
  const [newHashtag, setNewHashtag]   = useState('');
  const [suggesting, setSuggesting]   = useState(false);
  const [suggestions, setSuggestions] = useState([]);  // [{tag, score}]

  const [form, setForm] = useState({
    platform:    'instagram',
    content:     '',
    url:         '',
    mediaUrl:    '',
    hashtags:    [],
    status:      'draft',
    scheduledAt: '',   // ISO datetime-local string
    visible:     true,
    order:       '',
    notes:       '',
    postedToX:   false,
    postedAt:    null,  // Firestore Timestamp – read-only display
  });

  /* ── Load for edit ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'social_posts', id));
        if (!snap.exists()) {
          toast.error('Post not found');
          navigate('/admin/social');
          return;
        }
        const d = snap.data();
        // Convert Firestore Timestamp → datetime-local string
        let scheduledAt = '';
        if (d.scheduledAt) {
          const date = d.scheduledAt.toDate?.() ?? new Date(d.scheduledAt);
          // datetime-local expects "YYYY-MM-DDTHH:mm"
          scheduledAt = date.toISOString().slice(0, 16);
        }
        setForm({
          platform:    d.platform    || 'instagram',
          content:     d.content     || '',
          url:         d.url         || '',
          mediaUrl:    d.mediaUrl    || '',
          hashtags:    d.hashtags    || [],
          status:      d.status      || 'draft',
          scheduledAt,
          visible:     d.visible !== false,
          order:       d.order !== undefined ? String(d.order) : '',
          notes:       d.notes       || '',
          postedToX:   d.postedToX   ?? false,
          postedAt:    d.postedAt    ?? null,
        });
      } catch {
        toast.error('Failed to load post');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  /* ── Helpers ───────────────────────────────────────────────────── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const addHashtag = () => {
    const tag = newHashtag.trim().replace(/^#/, '');
    if (!tag) return;
    if (form.hashtags.includes(tag)) { setNewHashtag(''); return; }
    setForm(prev => ({ ...prev, hashtags: [...prev.hashtags, tag] }));
    setNewHashtag('');
  };

  const removeHashtag = (idx) =>
    setForm(prev => ({ ...prev, hashtags: prev.hashtags.filter((_, i) => i !== idx) }));

  /* ── Gemini Embedding 2 – AI hashtag suggestions ──────────────── */
  const suggestHashtags = async () => {
    if (!form.content.trim()) { toast.error('Write some content first'); return; }
    setSuggesting(true);
    setSuggestions([]);
    try {
      const { data } = await api.post('/api/v1/social/suggest-hashtags', {
        content: form.content,
        current_hashtags: form.hashtags,
        top_n: 6,
      });
      setSuggestions(data.suggestions ?? []);
      if (!data.suggestions?.length) toast('No new suggestions found', { icon: '🤷' });
    } catch {
      toast.error('AI suggestion failed – check backend connection');
    } finally {
      setSuggesting(false);
    }
  };

  const addSuggestedTag = (tag) => {
    if (form.hashtags.includes(tag)) return;
    setForm(prev => ({ ...prev, hashtags: [...prev.hashtags, tag] }));
    setSuggestions(prev => prev.filter(s => s.tag !== tag));
  };

  /* ── Media upload ─────────────────────────────────────────────── */
  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const storRef = ref(storage, `social-media/${Date.now()}_${file.name}`);
    const task    = uploadBytesResumable(storRef, file);
    task.on(
      'state_changed',
      snap => setUploadPct(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      err  => { toast.error('Upload failed: ' + err.code); setUploadPct(null); },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        setForm(prev => ({ ...prev, mediaUrl: url }));
        setUploadPct(null);
        toast.success('Media uploaded');
      },
    );
    e.target.value = '';
  };

  /* ── Save ──────────────────────────────────────────────────────── */
  const handleSave = async () => {
    if (!form.content.trim()) { toast.error('Content is required'); return; }

    setSaving(true);
    try {
      const payload = {
        platform:   form.platform,
        content:    form.content.trim(),
        url:        form.url.trim(),
        mediaUrl:   form.mediaUrl.trim(),
        hashtags:   form.hashtags,
        status:     form.status,
        scheduledAt: form.scheduledAt
          ? Timestamp.fromDate(new Date(form.scheduledAt))
          : null,
        visible:    form.visible,
        order:      form.order !== '' ? Number(form.order) : null,
        notes:      form.notes.trim(),
        updatedAt:  serverTimestamp(),
        postedToX:  form.postedToX,
        // When manually un-marking, clear the tweet metadata too
        ...(form.postedToX === false && { postedAt: null, tweetId: null }),
      };

      if (isEdit) {
        await updateDoc(doc(db, 'social_posts', id), payload);
        toast.success('Post updated!');
      } else {
        payload.createdAt = serverTimestamp();
        await addDoc(collection(db, 'social_posts'), payload);
        toast.success('Post created!');
      }
      navigate('/admin/social');
    } catch (err) {
      toast.error('Failed to save post: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-gray-400">
        <Loader2 size={24} className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 sm:mb-8">
        <button
          onClick={() => navigate('/admin/social')}
          className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-800">
            {isEdit ? 'Edit Social Post' : 'New Social Post'}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isEdit ? 'Update social post details' : 'Create a new social post'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">

        {/* ── Main ──────────────────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-5 order-2 lg:order-1">

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Platform</label>
              <select
                name="platform"
                value={form.platform}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Content <span className="text-red-400">*</span>
              </label>
              <textarea
                name="content"
                rows={6}
                value={form.content}
                onChange={handleChange}
                placeholder="Write your post content here…"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{form.content.length} chars</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Post URL
                <span className="ml-1 text-gray-400 font-normal text-xs">(link to share with the post)</span>
              </label>
              <input
                type="url"
                name="url"
                value={form.url}
                onChange={handleChange}
                placeholder="https://your-site.com/blog/post-slug"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Media */}
          <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 space-y-4">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2 text-sm">
              <Image className="w-4 h-4" /> Media
            </h2>

            {form.mediaUrl && (
              <div className="relative inline-block w-full">
                <img
                  src={form.mediaUrl}
                  alt="post media"
                  className="w-full max-h-56 object-cover rounded-xl border border-gray-100"
                />
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, mediaUrl: '' }))}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            <input type="file" ref={fileRef} accept="image/*,video/*" onChange={handleFilePick} className="hidden" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors text-sm"
            >
              <Upload className="w-4 h-4" />
              Upload Image / Video
            </button>

            {uploadPct !== null && (
              <div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${uploadPct}%` }} />
                </div>
                <p className="text-xs text-gray-500 mt-1">{uploadPct}% uploaded</p>
              </div>
            )}

            <div>
              <label className="block text-xs text-gray-500 mb-1">Or paste media URL</label>
              <input
                type="url"
                name="mediaUrl"
                value={form.mediaUrl}
                onChange={handleChange}
                placeholder="https://…"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Hashtags */}
          <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-4">
              Hashtags
              <span className="ml-1 text-gray-400 font-normal text-xs">({form.hashtags.length})</span>
            </label>

            <div className="flex gap-2 mb-3">
              <div className="flex-1 flex items-center border border-gray-200 rounded-xl overflow-hidden bg-white focus-within:ring-2 focus-within:ring-blue-500">
                <span className="px-3 text-gray-400 text-sm">#</span>
                <input
                  value={newHashtag}
                  onChange={e => setNewHashtag(e.target.value.replace(/^#/, ''))}
                  onKeyDown={e => e.key === 'Enter' && addHashtag()}
                  placeholder="hashtag"
                  className="flex-1 py-2.5 pr-3 text-sm focus:outline-none"
                />
              </div>
              <button
                onClick={addHashtag}
                className="px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm hover:bg-blue-700 transition-colors"
              >
                <Plus size={15} />
              </button>
            </div>

            {/* ── Gemini Embedding 2 suggest button ── */}
            <button
              type="button"
              onClick={suggestHashtags}
              disabled={suggesting || !form.content.trim()}
              className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-xl hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-4"
            >
              {suggesting
                ? <><Loader2 size={12} className="animate-spin" /> Gemini is thinking…</>
                : <><Wand2 size={12} /> AI Suggest with Gemini Embedding 2</>}
            </button>

            {/* ── AI suggestions ── */}
            {suggestions.length > 0 && (
              <div className="mb-4 p-3 bg-purple-50 rounded-xl border border-purple-100">
                <p className="text-xs font-semibold text-purple-700 mb-2">Gemini suggestions — click to add:</p>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.map(({ tag, score }) => (
                    <button
                      key={tag}
                      onClick={() => addSuggestedTag(tag)}
                      className="flex items-center gap-1 px-2.5 py-1 bg-white border border-purple-200 text-purple-700 rounded-full text-xs font-medium hover:bg-purple-700 hover:text-white hover:border-purple-700 transition-colors"
                    >
                      <Plus size={10} /> #{tag}
                      <span className="text-purple-400 text-[10px] ml-0.5">{(score * 100).toFixed(0)}%</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {form.hashtags.map((tag, idx) => (
                <span key={idx} className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                  #{tag}
                  <button onClick={() => removeHashtag(idx)} className="hover:text-red-500 transition-colors ml-0.5">
                    <X size={12} />
                  </button>
                </span>
              ))}
              {form.hashtags.length === 0 && (
                <p className="text-xs text-gray-400">No hashtags yet</p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Internal Notes
              <span className="ml-1 text-gray-400 font-normal text-xs">(not shown publicly)</span>
            </label>
            <textarea
              name="notes"
              rows={3}
              value={form.notes}
              onChange={handleChange}
              placeholder="Campaign context, target audience, approval notes…"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>
        </div>

        {/* ── Sidebar ───────────────────────────────────────────────── */}
        <div className="space-y-4 sm:space-y-5 order-1 lg:order-2">

          {/* Publish + Settings */}
          <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Settings</h3>

            {/* Posted to X toggle */}
            <label
              className="flex items-center gap-3 cursor-pointer mb-4"
              onClick={() => setForm(prev => ({ ...prev, postedToX: !prev.postedToX }))}
            >
              <div className={`relative w-10 h-5 rounded-full transition-colors ${form.postedToX ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.postedToX ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
              <span className="text-sm text-gray-600">
                {form.postedToX ? 'Posted to X' : 'Not posted to X'}
              </span>
            </label>

            {/* Posted at – read-only display */}
            {form.postedAt && (
              <p className="text-xs text-gray-400 mb-4 -mt-2 pl-[52px]">
                {form.postedAt.toDate
                  ? form.postedAt.toDate().toLocaleString()
                  : new Date(form.postedAt).toLocaleString()}
              </p>
            )}

            {/* Visible toggle */}
            <label
              className="flex items-center gap-3 cursor-pointer mb-4"
              onClick={() => setForm(prev => ({ ...prev, visible: !prev.visible }))}
            >
              <div className={`relative w-10 h-5 rounded-full transition-colors ${form.visible ? 'bg-green-500' : 'bg-gray-300'}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.visible ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
              <span className="text-sm text-gray-600">{form.visible ? 'Visible on site' : 'Hidden'}</span>
            </label>

            {/* Status */}
            <div className="mb-4">
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            {/* Scheduled date */}
            <div className="mb-5">
              <label className="block text-xs text-gray-500 mb-1">Scheduled Date &amp; Time</label>
              <input
                type="datetime-local"
                name="scheduledAt"
                value={form.scheduledAt}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              {saving
                ? <><Loader2 size={14} className="animate-spin" /> Saving…</>
                : <><Save size={14} /> {isEdit ? 'Update Post' : 'Create Post'}</>
              }
            </button>
          </div>

          {/* Sort order */}
          <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Sort Order
              <span className="ml-1 text-gray-400 font-normal text-xs">(lower = first)</span>
            </label>
            <input
              name="order"
              type="number"
              value={form.order}
              onChange={handleChange}
              placeholder="1"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
